import { ParsedUrlQuery } from 'querystring';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { AxiosResponse } from 'axios';
import { isEqualWith } from 'lodash';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import queryString from 'query-string';

import { signInModalInstance } from 'components/candidate/SignInModal';
import { listNameModalInstance } from 'components/search/results/ManageSavedLists/ManageSavedLists.component';
import useDebounce from 'hooks/useDebounce';
import { SearchPageType, SearchPageCategory, SearchPageSortBy, SearchPageSortOrder } from 'lib/utils/global.search';
import { API_ROUTES } from 'lib/api-routes';
import { apiInstance } from 'lib/utils/axios';
import { BasicCompaniesResponse } from 'lib/models/company';
import {
  getAxiosPaginatedResponseTotal,
  getAxiosPaginatedResponseTotalV2,
  parseArrayResponse,
  parseAxiosArrayResponseV2,
  parseAxiosPaginatedResponse,
  parseAxiosPaginatedResponseV2,
} from 'lib/utils/parser';
import { Option } from 'lib/models/option';
import { useCommonList } from 'lib/contexts/common-list-context';
import { BasicOrganizationsResponse } from 'lib/models/organization';
import { TSearchList, TSearchListRequest, TSearchQueryObject } from 'lib/models/search-page';
import { addCandidateSearchList, deleteCandidateSearchList, updateCandidateSearchList } from 'lib/services/candidate';
import { useAuth } from 'lib/providers/AuthProvider';
import { PAGE_ROUTES } from 'lib/page-routes';
import { TQueryParams, getOpportunityFilterQueryParams, getTypeFilterQueryParams } from 'utils/search';

export enum UserActionEnum {
  SAVE_LIST = 'SAVE_LIST',
}

type UpdateUserActionProps = {
  userAction: UserActionEnum.SAVE_LIST | null;
};

export enum FilterableField {
  DATE_POSTED = 'datePosted',
  ROLE_TYPE = 'areaOfResponsibility',
  LOCATION = 'location',
  WORK_MODE = 'workMode',
  WORK_TYPE = 'workType',
  POSTED_BY_COMPANIES = 'postedByCompanies',
  POSTED_BY_ORGANIZATIONS = 'postedByOrganizations',
  SALARY = 'salary',
  EXPERIENCE_LEVEL = 'experienceLevel',
  INTERNSHIP_TYPES = 'internshipTypes',
  TOPICS = 'topics',
  EVENT_CATEGORIES = 'eventCategories',
  EVENT_TYPES = 'eventTypes',
  INDUSTRIES = 'industries',
  UNIVERISTIES = 'universities',
  OFFERINGS = 'offerings',
  VERIFIED = 'verified',
  COMPANY_SIZES = 'companySizes',
  DEADLINE_VISIBLE = 'deadlineVisible',
  EVENT_MODE = 'eventMode',
  EASY_REGISTER = 'easyRegister',
  QUICK_APPLY = 'quickApply',
}

export enum FilterableFieldTypes {
  MULTI_SELECT_LOCATION = 'multiSelectLocation',
  SINGLE_SELECT_TICK = 'singleSelectTick',
  MULTI_SELECT_TICK = 'multiSelectTick',
  MULTI_SELECT_CHIP = 'multiSelectChip',
  TOGGLE = 'toggle',
}

export type FilterableFieldSetting = {
  id: FilterableField;
  labelKey: string;
  type: FilterableFieldTypes;
  isSearchable: boolean;
  param: string;
  subParam?: string;
  fetchMoreData?: () => void;
  onSearch?: (searchText: string) => void;
  showLoader?: boolean;
  className?: string;
};

export const FilterableFieldSettings: Record<FilterableField, FilterableFieldSetting> = {
  [FilterableField.DATE_POSTED]: {
    id: FilterableField.DATE_POSTED,
    labelKey: 'filterable-fields.date-posted',
    type: FilterableFieldTypes.SINGLE_SELECT_TICK,
    isSearchable: false,
    param: 'posted_after',
  },
  [FilterableField.ROLE_TYPE]: {
    id: FilterableField.ROLE_TYPE,
    labelKey: 'filterable-fields.role-type',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: true,
    param: 'area_of_responsibility_ids',
  },
  [FilterableField.LOCATION]: {
    id: FilterableField.LOCATION,
    labelKey: 'filterable-fields.location',
    type: FilterableFieldTypes.MULTI_SELECT_LOCATION,
    isSearchable: true,
    param: 'country_ids',
    subParam: 'city_ids',
  },
  [FilterableField.WORK_MODE]: {
    id: FilterableField.WORK_MODE,
    labelKey: 'filterable-fields.work-mode',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'working_model',
  },
  [FilterableField.WORK_TYPE]: {
    id: FilterableField.WORK_TYPE,
    labelKey: 'filterable-fields.work-type',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'work_type',
  },
  [FilterableField.POSTED_BY_COMPANIES]: {
    id: FilterableField.POSTED_BY_COMPANIES,
    labelKey: 'filterable-fields.posted-by',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: true,
    param: 'posted_by_company_ids', // Change this to creatable_for_ids while sending it to backend
  },
  [FilterableField.POSTED_BY_ORGANIZATIONS]: {
    id: FilterableField.POSTED_BY_ORGANIZATIONS,
    labelKey: 'filterable-fields.posted-by',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: true,
    param: 'posted_by_organization_ids',
  },
  [FilterableField.SALARY]: {
    id: FilterableField.SALARY,
    labelKey: 'filterable-fields.salary',
    type: FilterableFieldTypes.SINGLE_SELECT_TICK,
    isSearchable: false,
    param: 'minimum_salary',
  },
  [FilterableField.EXPERIENCE_LEVEL]: {
    id: FilterableField.EXPERIENCE_LEVEL,
    labelKey: 'filterable-fields.experience-level',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'experience_levels',
  },
  [FilterableField.QUICK_APPLY]: {
    id: FilterableField.QUICK_APPLY,
    labelKey: 'filterable-fields.quick-apply',
    type: FilterableFieldTypes.TOGGLE,
    isSearchable: false,
    param: 'quick_apply',
  },
  [FilterableField.EASY_REGISTER]: {
    id: FilterableField.EASY_REGISTER,
    labelKey: 'filterable-fields.easy-register',
    type: FilterableFieldTypes.TOGGLE,
    isSearchable: false,
    param: 'easy_register',
  },
  [FilterableField.EVENT_MODE]: {
    id: FilterableField.EVENT_MODE,
    labelKey: 'filterable-fields.event-mode',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'is_online',
  },
  [FilterableField.DEADLINE_VISIBLE]: {
    id: FilterableField.DEADLINE_VISIBLE,
    labelKey: 'filterable-fields.deadline-visible',
    type: FilterableFieldTypes.TOGGLE,
    isSearchable: false,
    param: 'application_deadline_available',
  },
  [FilterableField.INTERNSHIP_TYPES]: {
    id: FilterableField.INTERNSHIP_TYPES,
    labelKey: 'filterable-fields.internship-type',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'internship_type',
  },
  [FilterableField.TOPICS]: {
    id: FilterableField.TOPICS,
    labelKey: 'filterable-fields.topics',
    type: FilterableFieldTypes.MULTI_SELECT_CHIP,
    isSearchable: true,
    param: 'topic_ids',
  },
  [FilterableField.EVENT_CATEGORIES]: {
    id: FilterableField.EVENT_CATEGORIES,
    labelKey: 'filterable-fields.event-category',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'event_category_ids',
  },
  [FilterableField.EVENT_TYPES]: {
    id: FilterableField.EVENT_TYPES,
    labelKey: 'filterable-fields.event-type',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'event_type',
  },
  [FilterableField.INDUSTRIES]: {
    id: FilterableField.INDUSTRIES,
    labelKey: 'filterable-fields.industry',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'industry_ids',
  },
  [FilterableField.UNIVERISTIES]: {
    id: FilterableField.UNIVERISTIES,
    labelKey: 'filterable-fields.university',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: true,
    param: 'university_ids',
  },
  [FilterableField.OFFERINGS]: {
    id: FilterableField.OFFERINGS,
    labelKey: 'filterable-fields.offerings',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'offering_ids',
  },
  [FilterableField.COMPANY_SIZES]: {
    id: FilterableField.COMPANY_SIZES,
    labelKey: 'filterable-fields.company-size',
    type: FilterableFieldTypes.MULTI_SELECT_TICK,
    isSearchable: false,
    param: 'employees_count',
  },
  [FilterableField.VERIFIED]: {
    id: FilterableField.VERIFIED,
    labelKey: 'filterable-fields.active-on-huzzle',
    type: FilterableFieldTypes.TOGGLE,
    isSearchable: false,
    param: 'verified',
  },
};

export const JobFilters = [
  FilterableField.QUICK_APPLY,
  FilterableField.DATE_POSTED,
  FilterableField.EXPERIENCE_LEVEL,
  FilterableField.ROLE_TYPE,
  FilterableField.LOCATION,
  FilterableField.WORK_MODE,
  FilterableField.WORK_TYPE,
  FilterableField.POSTED_BY_COMPANIES,
  FilterableField.SALARY,
  FilterableField.DEADLINE_VISIBLE,
];

export const JobsAndInternshipFilter = [
  FilterableField.QUICK_APPLY,
  FilterableField.DATE_POSTED,
  FilterableField.INTERNSHIP_TYPES,
  FilterableField.EXPERIENCE_LEVEL,
  FilterableField.ROLE_TYPE,
  FilterableField.LOCATION,
  FilterableField.WORK_MODE,
  FilterableField.WORK_TYPE,
  FilterableField.POSTED_BY_COMPANIES,
  FilterableField.SALARY,
  FilterableField.DEADLINE_VISIBLE,
];

export const InternshipFilters = [
  FilterableField.QUICK_APPLY,
  FilterableField.DATE_POSTED,
  FilterableField.INTERNSHIP_TYPES,
  FilterableField.ROLE_TYPE,
  FilterableField.LOCATION,
  FilterableField.WORK_MODE,
  FilterableField.WORK_TYPE,
  FilterableField.POSTED_BY_COMPANIES,
  FilterableField.SALARY,
  FilterableField.DEADLINE_VISIBLE,
];

export const EventFilters = [
  FilterableField.DATE_POSTED,
  FilterableField.EVENT_TYPES,
  FilterableField.EVENT_CATEGORIES,
  FilterableField.INDUSTRIES,
  FilterableField.LOCATION,
  FilterableField.EVENT_MODE,
  FilterableField.POSTED_BY_ORGANIZATIONS,
  FilterableField.TOPICS,
  FilterableField.EASY_REGISTER,
];

export const SocietyFilters = [FilterableField.UNIVERISTIES, FilterableField.TOPICS, FilterableField.VERIFIED];

export const CompanyFilters = [FilterableField.INDUSTRIES, FilterableField.COMPANY_SIZES];

export const useGlobalSearchInternal = ({
  requestedPageType,
  initialQueryParams,
}: {
  requestedPageType: SearchPageType | null;
  initialQueryParams: TQueryParams;
}) => {
  const router = useRouter();
  const { isCandidate, isInitialAuthLoading } = useAuth();
  const { replace, query } = router;
  const { type, sort_by, user_action: queryUserAction } = query;
  const [isSearchModalOpen, setSearchFilterModal] = useState(false);
  const [canShowOnboardingBanner, setOnboardingBanner] = useState(true);
  const [companySearchText, setCompanySearchText] = useState<string>('');
  const [organizationSearchText, setOrganizationSearchText] = useState<string>('');
  const [partialFilter, setPartialFilter] = useState<Record<string, string | string[] | null>>({});
  const [filterState, setFilterState] = useState<Record<string, string | string[] | null>>({});
  const [showSavedListsModal, setShowSavedListsModal] = useState(false);
  const [isListAdded, setIsListAdded] = useState(false);
  const { q } = query;
  const selectedCompanyIds = query['posted_by_company_ids'];
  const selectedOrganizationIds = query['posted_by_organization_ids'];
  const requestedPageTypeRef = useRef(requestedPageType);
  const [hasRequestedPageTypeChanged, setRequestPageTypeChanged] = useState(false);
  const initialQueryParamsReplaceLoadingRef = useRef(!!initialQueryParams);

  const searchQuery = q ? q.toString() : '';
  const trimmedSearch = searchQuery.trim();
  const debouncedSearch = useDebounce(trimmedSearch, 500);
  const debouncedCompanySearch = useDebounce(companySearchText, 200);
  const debouncedOrganizationSearch = useDebounce(organizationSearchText, 200);

  const COMPANY_PER_PAGE_COUNT = 50;
  const ORGANIZATIONS_PER_PAGE_COUNT = 50;
  const isCareerOpportunityPage = router.pathname === PAGE_ROUTES.CAREER_OPPORTUNITY;

  useEffect(() => {
    if (!initialQueryParams || !initialQueryParamsReplaceLoadingRef.current) return;

    async function init() {
      const params = Object.fromEntries(new URLSearchParams(location.search));
      const finalQueryParams = { ...params, ...initialQueryParams };
      const qs = queryString.stringify(finalQueryParams, {
        arrayFormat: 'none',
        skipEmptyString: true,
        skipNull: true,
      });
      const replacePath = `${location.pathname}?${qs}`;
      setTimeout(() => {
        replace(replacePath, undefined, { shallow: true });
        initialQueryParamsReplaceLoadingRef.current = false;
      }, 100);
    }

    try {
      init();
    } catch (error) {
      console.error('Error while replacing initial query params', error);
    }
  }, [initialQueryParams]);

  const {
    datePostedList,
    areaOfResponsibilities,
    workModeList,
    workTypeList,
    salaryOptions,
    experienceLevels,
    internshipKindOptions,
    eventTypeOptions,
    eventCategoriesOptions,
    eventModeOptions,
    industryTypeOptions,
    topicTypeOptions,
    offeringOptions,
    universities,
    employeesCountRange,
    countries,
    cityCountryList,
  } = useCommonList();

  const openSearchFilterModal = useCallback(() => {
    setSearchFilterModal(true);
  }, []);

  const closeSearchFilterModal = useCallback(() => {
    setSearchFilterModal(false);
  }, []);

  const closeOnboardingBanner = useCallback(() => {
    setOnboardingBanner(false);
  }, []);

  const openSavedListsModal = useCallback(() => {
    setShowSavedListsModal(true);
  }, []);

  const closeSavedListsModal = useCallback(() => {
    setShowSavedListsModal(false);
  }, []);

  const contentType = useMemo(() => {
    if (typeof type === 'undefined') return SearchPageType.JOBS;

    const typeString = Array.isArray(type) ? type[0] : type;
    if (Object.values(SearchPageType).includes(typeString as SearchPageType)) return typeString as SearchPageType;
    return SearchPageType.JOBS;
  }, [type]);

  const category = useMemo(() => {
    if (type === SearchPageType.COMPANIES || type === SearchPageType.COMMUNITIES) {
      return SearchPageCategory.ORGANIZATION;
    }
    return SearchPageCategory.OPPORTUNITY;
  }, [type]);

  const [sortBy, sortOrder] = useMemo(() => {
    if (category !== SearchPageCategory.OPPORTUNITY) return [null, null];
    if (typeof sort_by === 'undefined') return [SearchPageSortBy.MOST_RELEVANT, SearchPageSortOrder.DESC];

    const sortString = Array.isArray(sort_by) ? sort_by[0] : sort_by;
    if (Object.values(SearchPageSortBy).includes(sortString as SearchPageSortBy)) return [sortString as SearchPageSortBy, SearchPageSortOrder.DESC];
    return [SearchPageSortBy.MOST_RELEVANT, SearchPageSortOrder.DESC];
  }, [category, sort_by]);

  const fetchCompanyByIdsURI = () => {
    const params = {
      ids: selectedCompanyIds,
    };
    const query = queryString.stringify(params, {
      arrayFormat: 'bracket',
      skipEmptyString: true,
    });
    return `${API_ROUTES.COMPANIES}?${query}`;
  };

  const { data: selectedCompaniesDataResponse } = useSWR<AxiosResponse<BasicCompaniesResponse>>(
    selectedCompanyIds ? fetchCompanyByIdsURI : null,
    apiInstance.get
  );

  const selectedCompanies = useMemo(() => {
    if (!selectedCompaniesDataResponse) return;

    return parseArrayResponse(selectedCompaniesDataResponse.data).map((company) => {
      return {
        id: company.id,
        value: company.id,
        label: company.name,
      } as Option;
    });
  }, [selectedCompaniesDataResponse]);

  const fetchCompaniesURI = (pageIndex: number) => {
    const params = {
      name: debouncedCompanySearch,
      page: pageIndex + 1,
      per_page: COMPANY_PER_PAGE_COUNT,
    };
    const qs = queryString.stringify(params, {
      arrayFormat: 'bracket',
      skipEmptyString: true,
    });

    return `${API_ROUTES.COMPANIES}?${qs}`;
  };

  const {
    data: companiesListResponse,
    error: companiesError,
    size,
    setSize,
    isValidating: isCompaniesDataLoading,
  } = useSWRInfinite<AxiosResponse<BasicCompaniesResponse>>(fetchCompaniesURI, apiInstance.get, {
    persistSize: false,
    revalidateOnFocus: false,
    revalidateFirstPage: false,
  });

  const companiesOptions = useMemo(() => {
    if (!companiesListResponse) return;
    const companies = parseAxiosPaginatedResponse(companiesListResponse);
    let options;
    if (selectedCompanies && selectedCompanyIds && !debouncedCompanySearch) {
      const companyIds = Array.isArray(selectedCompanyIds) ? selectedCompanyIds : [selectedCompanyIds];
      options = companies.reduce((optionArr, company) => {
        if (!companyIds.includes(company.id)) {
          optionArr.push({
            id: company.id,
            value: company.id,
            label: company.name,
          });
        }
        return optionArr;
      }, [] as Option[]);
      options.unshift(...selectedCompanies);
    } else {
      options = companies.map((company) => {
        return {
          id: company.id,
          value: company.id,
          label: company.name,
        } as Option;
      });
    }

    return options as Option[];
  }, [companiesListResponse, selectedCompanies, debouncedCompanySearch]);

  const fetchMoreCompanies = useCallback(() => {
    if (!isCompaniesDataLoading && !companiesError && companiesOptions?.length === size * COMPANY_PER_PAGE_COUNT) {
      const total = getAxiosPaginatedResponseTotal(companiesListResponse);
      const hasMoreRecords = total > size * COMPANY_PER_PAGE_COUNT;
      if (hasMoreRecords) {
        setSize((oldSize) => oldSize + 1);
      }
    }
  }, [isCompaniesDataLoading, companiesError, companiesOptions, companiesListResponse, size, setSize]);

  const fetchOrganizationByIdsURI = () => {
    const params = {
      ids: Array.isArray(selectedOrganizationIds) ? selectedOrganizationIds : [selectedOrganizationIds],
    };
    const query = queryString.stringify(params, {
      arrayFormat: 'bracket',
      skipEmptyString: true,
    });
    return `${API_ROUTES.CREATABLE_FOR}?${query}`;
  };

  const { data: selectedOrganizationsDataResponse } = useSWR<AxiosResponse<BasicOrganizationsResponse>>(
    selectedOrganizationIds ? fetchOrganizationByIdsURI : null,
    apiInstance.get
  );

  const selectedOrganizations = useMemo(() => {
    if (!selectedOrganizationsDataResponse) return;

    return parseAxiosArrayResponseV2(selectedOrganizationsDataResponse).map((org) => {
      return {
        id: org.id,
        value: org.id,
        label: org.name,
      } as Option;
    });
  }, [selectedOrganizationsDataResponse]);

  const fetchOrganizationsURI = (pageIndex: number) => {
    const params = {
      q: debouncedOrganizationSearch,
      page: pageIndex + 1,
      per_page: ORGANIZATIONS_PER_PAGE_COUNT,
    };
    const qs = queryString.stringify(params, {
      arrayFormat: 'bracket',
      skipEmptyString: true,
    });

    return `${API_ROUTES.CREATABLE_FOR}?${qs}`;
  };

  const {
    data: organizationsListResponse,
    error: organizationsError,
    size: organizationsSize,
    setSize: setOrganizationsSize,
    isValidating: isOrganizationsDataLoading,
  } = useSWRInfinite<AxiosResponse<BasicOrganizationsResponse>>(fetchOrganizationsURI, apiInstance.get, {
    persistSize: false,
    revalidateOnFocus: false,
    revalidateFirstPage: false,
  });

  const organizationsOptions = useMemo(() => {
    if (!organizationsListResponse) return;
    const organizations = parseAxiosPaginatedResponseV2(organizationsListResponse);
    let options;
    if (selectedOrganizations && selectedOrganizationIds && !debouncedOrganizationSearch) {
      const organizationIds = Array.isArray(selectedOrganizationIds) ? selectedOrganizationIds : [selectedOrganizationIds];
      options = organizations.reduce((optionArr, org) => {
        if (!organizationIds.includes(org.id)) {
          optionArr.push({
            id: org.id,
            value: org.id,
            label: org.name,
          });
        }
        return optionArr;
      }, [] as Option[]);
      options = [...selectedOrganizations, ...options];
    } else {
      options = organizations.map((organization) => {
        return {
          id: organization.id,
          value: organization.id,
          label: organization.name,
        } as Option;
      });
    }

    return options as Option[];
  }, [organizationsListResponse, selectedOrganizations, debouncedOrganizationSearch]);

  const fetchMoreOrganizations = useCallback(() => {
    if (!isOrganizationsDataLoading && !organizationsError && organizationsOptions?.length === organizationsSize * ORGANIZATIONS_PER_PAGE_COUNT) {
      const total = getAxiosPaginatedResponseTotalV2(organizationsListResponse);
      const hasMoreRecords = total > organizationsSize * ORGANIZATIONS_PER_PAGE_COUNT;
      if (hasMoreRecords) {
        setOrganizationsSize((oldSize) => oldSize + 1);
      }
    }
  }, [isOrganizationsDataLoading, organizationsError, organizationsOptions, organizationsListResponse, organizationsSize, setOrganizationsSize]);

  const getPostedByOrganizationFilterAttrs = useCallback(
    (filter: FilterableField) => {
      return {
        ...FilterableFieldSettings[filter],
        fetchMoreData: fetchMoreOrganizations,
        onSearch: setOrganizationSearchText,
        showLoader: isOrganizationsDataLoading,
        className: 'posted-by-dropdown',
      };
    },
    [fetchMoreOrganizations, isOrganizationsDataLoading]
  );

  const getPostedByCompanyFilterAttrs = useCallback(
    (filter: FilterableField) => {
      return {
        ...FilterableFieldSettings[filter],
        fetchMoreData: fetchMoreCompanies,
        onSearch: setCompanySearchText,
        showLoader: isCompaniesDataLoading,
        className: 'posted-by-dropdown',
      };
    },
    [fetchMoreCompanies, isCompaniesDataLoading]
  );
  const currentFilters = useMemo(() => {
    switch (contentType) {
      case SearchPageType.JOBS:
        return JobFilters.map((filter) =>
          filter === FilterableField.POSTED_BY_COMPANIES ? getPostedByCompanyFilterAttrs(filter) : FilterableFieldSettings[filter]
        );
      case SearchPageType.JOBS_AND_INTERNSHIPS:
        return JobsAndInternshipFilter.filter((filter) => {
          // Remove EXPERIENCE_LEVEL if internshipType is selected and experienceLevel is not selected
          if (partialFilter.internship_type && partialFilter.internship_type?.length > 0 && filter === FilterableField.EXPERIENCE_LEVEL) {
            return false;
          }
          // Remove INTERNSHIP_TYPES if experienceLevel is selected and internshipType is not selected
          if (partialFilter.experience_levels && partialFilter?.experience_levels?.length > 0 && filter === FilterableField.INTERNSHIP_TYPES) {
            return false;
          }
          // Keep the filter otherwise
          return true;
        }).map((filter) =>
          filter === FilterableField.POSTED_BY_COMPANIES ? getPostedByCompanyFilterAttrs(filter) : FilterableFieldSettings[filter]
        );
      case SearchPageType.INTERNSHIPS:
        return InternshipFilters.map((filter) =>
          filter === FilterableField.POSTED_BY_COMPANIES ? getPostedByCompanyFilterAttrs(filter) : FilterableFieldSettings[filter]
        );
      case SearchPageType.EVENTS:
        return EventFilters.map((filter) =>
          filter === FilterableField.POSTED_BY_ORGANIZATIONS ? getPostedByOrganizationFilterAttrs(filter) : FilterableFieldSettings[filter]
        );
      case SearchPageType.COMMUNITIES:
        return SocietyFilters.map((filter) => FilterableFieldSettings[filter]);
      case SearchPageType.COMPANIES:
        return CompanyFilters.map((filter) => FilterableFieldSettings[filter]);
      default:
        return [];
    }
  }, [contentType, getPostedByCompanyFilterAttrs, getPostedByOrganizationFilterAttrs, partialFilter]);

  const replaceQuery = useCallback(
    (param: Record<string, string | string[] | boolean | null>) => {
      const newQuery = { ...query };
      Object.entries(param).forEach(([key, value]) => {
        if (value) {
          if (value === true) {
            newQuery[key] = String(value);
          } else {
            newQuery[key] = value;
          }
        } else {
          delete newQuery[key];
        }
      });
      const oldQueryString = queryString.stringify(query, {
          skipEmptyString: true,
        }),
        newQueryString = queryString.stringify(newQuery, {
          skipEmptyString: true,
        });
      if (oldQueryString !== newQueryString) {
        replace({ query: newQueryString }, undefined, { shallow: true });
      }
    },
    [query]
  );

  const getFieldOptions = useCallback(
    (fieldSetting: FilterableFieldSetting) => {
      switch (fieldSetting.id) {
        case FilterableField.DATE_POSTED:
          return datePostedList;
        case FilterableField.WORK_MODE:
          return workModeList;
        case FilterableField.ROLE_TYPE:
          return areaOfResponsibilities;
        case FilterableField.WORK_TYPE:
          return workTypeList;
        case FilterableField.SALARY:
          return salaryOptions;
        case FilterableField.EXPERIENCE_LEVEL:
          return experienceLevels;
        case FilterableField.INTERNSHIP_TYPES:
          return internshipKindOptions;
        case FilterableField.EVENT_TYPES:
          return eventTypeOptions;
        case FilterableField.EVENT_CATEGORIES:
          return eventCategoriesOptions;
        case FilterableField.EVENT_MODE:
          return eventModeOptions;
        case FilterableField.INDUSTRIES:
          return industryTypeOptions;
        case FilterableField.TOPICS:
          return topicTypeOptions;
        case FilterableField.OFFERINGS:
          return offeringOptions;
        case FilterableField.UNIVERISTIES:
          return universities;
        case FilterableField.COMPANY_SIZES:
          return employeesCountRange;
        case FilterableField.POSTED_BY_COMPANIES:
          return companiesOptions;
        case FilterableField.POSTED_BY_ORGANIZATIONS:
          return organizationsOptions;
        case FilterableField.LOCATION:
          return countries;
        default:
          return [];
      }
    },
    [
      datePostedList,
      workModeList,
      areaOfResponsibilities,
      workTypeList,
      salaryOptions,
      experienceLevels,
      internshipKindOptions,
      eventTypeOptions,
      eventCategoriesOptions,
      industryTypeOptions,
      topicTypeOptions,
      offeringOptions,
      universities,
      employeesCountRange,
      organizationsOptions,
      companiesOptions,
    ]
  );

  const getFieldSubOptions = useCallback(
    (fieldSetting: FilterableFieldSetting) => {
      switch (fieldSetting.id) {
        case FilterableField.LOCATION: {
          return cityCountryList.map((cityCountry) => ({
            id: cityCountry.id,
            label: cityCountry.label,
            value: cityCountry.id,
            parent_id: cityCountry.country_id,
          }));
        }
        default:
          return [];
      }
    },
    [cityCountryList]
  );

  const getCurrentOpportunityPageQuery = useCallback(() => {
    let qp: TSearchQueryObject | null = null;
    switch (contentType) {
      case SearchPageType.JOBS:
        qp = getOpportunityFilterQueryParams({ filters: JobFilters, query });
        break;
      case SearchPageType.INTERNSHIPS:
        qp = getOpportunityFilterQueryParams({ filters: InternshipFilters, query });
        break;
      case SearchPageType.EVENTS:
        qp = getOpportunityFilterQueryParams({ filters: EventFilters, query });
        break;
      default:
        qp = null;
    }

    return qp ? { ...qp, type: contentType } : null;
  }, [contentType, query]);

  const currentOpportunityPageQuery = useMemo(() => getCurrentOpportunityPageQuery(), [getCurrentOpportunityPageQuery]);

  const applySavedListQuery = useCallback(
    (filterQuery: TSearchQueryObject) => {
      const nonFilterQuery = Object.keys(query).reduce((acc, key) => {
        if (Object.keys(query).includes(key)) {
          if (!Object.values(FilterableFieldSettings).some((value) => value.param === key || value.subParam === key)) {
            acc[key] = query[key];
            return acc;
          }
          return acc;
        }
        return acc;
      }, {} as ParsedUrlQuery);
      const finalQuery = { ...nonFilterQuery, ...filterQuery };
      replace({ query: finalQuery }, undefined, { shallow: true });
    },
    [query]
  );

  const handleTypeChange = useCallback(
    (type: string) => {
      let newQuery: Record<string, string | string[] | undefined>;
      switch (type) {
        case SearchPageType.INTERNSHIPS:
          newQuery = getTypeFilterQueryParams({ filters: InternshipFilters, query });
          break;
        case SearchPageType.EVENTS:
          newQuery = getTypeFilterQueryParams({ filters: EventFilters, query });
          break;
        case SearchPageType.COMMUNITIES:
          newQuery = getTypeFilterQueryParams({ filters: SocietyFilters, query });
          break;
        case SearchPageType.COMPANIES:
          newQuery = getTypeFilterQueryParams({ filters: CompanyFilters, query });
          break;
        case SearchPageType.JOBS:
        default:
          newQuery = getTypeFilterQueryParams({ filters: JobFilters, query });
          break;
      }
      newQuery.type = type;
      replace({ query: newQuery }, undefined, { shallow: true });
    },
    [query]
  );

  useEffect(() => {
    if (hasRequestedPageTypeChanged || typeof type === 'undefined') return;

    if (requestedPageType !== type) {
      setRequestPageTypeChanged(true);
    }
  }, [type, requestedPageType, hasRequestedPageTypeChanged]);

  const onPartialChange = useCallback(
    ({ filter, value, subValue }: { filter: FilterableFieldSetting; value?: string | string[] | null; subValue?: string | string[] | null }) => {
      if (typeof value !== 'undefined' && typeof subValue !== 'undefined' && filter.subParam) {
        setPartialFilter({ [filter.param]: value, [filter.subParam]: subValue });
      } else if (typeof subValue !== 'undefined' && filter.subParam) {
        setPartialFilter({ [filter.subParam]: subValue });
      } else if (typeof value !== 'undefined') {
        setPartialFilter({ [filter.param]: value });
      } else {
        setPartialFilter({});
      }
    },
    [setPartialFilter]
  );

  const onPartialUpdate = useCallback(
    ({ filter, value, subValue }: { filter: FilterableFieldSetting; value?: string | string[] | null; subValue?: string | string[] | null }) => {
      if (typeof value !== 'undefined' && typeof subValue !== 'undefined' && filter.subParam) {
        setPartialFilter((oldFilter) => ({ ...oldFilter, [filter.param]: value, [filter.subParam as string]: subValue }));
      } else if (typeof subValue !== 'undefined' && filter.subParam) {
        setPartialFilter((oldFilter) => ({ ...oldFilter, [filter.subParam as string]: subValue }));
      } else if (typeof value !== 'undefined') {
        setPartialFilter((oldFilter) => ({ ...oldFilter, [filter.param]: value }));
      } else {
        setPartialFilter(({ [filter.param]: _value, ...rest }) => ({ ...rest }));
      }
    },
    [setPartialFilter]
  );

  const onPartialFiltersUpdate = useCallback(
    (filters: Record<string, string | string[] | null>) => {
      setPartialFilter(filters);
    },
    [setPartialFilter]
  );

  const onResetPartialFilters = useCallback(() => {
    if (!isCareerOpportunityPage) setPartialFilter({});
  }, [setPartialFilter, isCareerOpportunityPage]);

  const onResetAppliedFilters = useCallback(() => {
    const newQuery = { ...query };
    currentFilters.forEach((filter) => {
      delete newQuery[filter.param];
      if (filter.subParam) {
        delete newQuery[filter.subParam];
      }
    });
    replace({ query: newQuery }, undefined, { shallow: true });
  }, []);

  const handleSearchChange = useCallback((value: string) => replaceQuery({ q: value }), [replaceQuery]);
  const handleClearSearch = useCallback(() => replaceQuery({ q: '' }), [replaceQuery]);

  const handleFieldValueChange = useCallback(
    ({
      param,
      value,
      subParam,
      subValue,
    }: {
      param: string;
      subParam?: string;
      value: string | string[] | boolean;
      subValue?: string | string[] | boolean;
    }) => {
      if (subParam && subValue !== undefined) {
        replaceQuery({ [param]: value, [subParam]: subValue! });
      } else {
        replaceQuery({ [param]: value });
      }
    },
    [replaceQuery]
  );

  const onApplyPartialFilter = useCallback(() => {
    if (isCareerOpportunityPage) {
      setFilterState({ ...partialFilter });
    } else {
      replaceQuery(partialFilter);
      setPartialFilter({});
    }
  }, [replaceQuery, partialFilter, closeSearchFilterModal, filterState, isCareerOpportunityPage]);

  const isPartialFiltersApplied = useMemo(() => Object.keys(partialFilter).length > 0, [partialFilter]);
  const isFiltersApplied = useMemo(() => {
    return currentFilters.some((filter) => {
      const value = query[filter.param];
      return value !== undefined && value !== null && value !== '';
    });
  }, [query, currentFilters]);

  const handleSortByChange = useCallback(
    (value: string) => {
      replaceQuery({ sort_by: value });
    },
    [replaceQuery]
  );

  const { data: candidateSearchListsResponse, mutate: mutateCandidateSearchListsResponse } = useSWR<AxiosResponse<TSearchList[]>>(
    isCandidate ? API_ROUTES.CANDIDATE_SEARCH_LISTS : null,
    apiInstance.get,
    {
      revalidateOnFocus: false,
    }
  );
  const searchLists = useMemo(() => candidateSearchListsResponse?.data || [], [candidateSearchListsResponse]);

  const handleDeleteList = useCallback(
    async ({ listId }: { listId: string }) => {
      await deleteCandidateSearchList({ listId });
      mutateCandidateSearchListsResponse(
        (prevData) => {
          if (!prevData) return;
          return {
            ...prevData,
            data: prevData.data.filter((list) => list.id !== listId),
          };
        },
        { revalidate: false }
      );
    },
    [mutateCandidateSearchListsResponse]
  );

  const handleUpdateList = useCallback(
    async ({ listId, data }: { listId: string; data: Partial<TSearchListRequest> }) => {
      const response = await updateCandidateSearchList(listId, data);
      mutateCandidateSearchListsResponse(
        (prevData) => {
          if (!prevData) return;
          return {
            ...prevData,
            data: prevData.data.map((list) => {
              if (list.id === listId) {
                return {
                  ...list,
                  ...response.data,
                };
              }
              return list;
            }),
          };
        },
        { revalidate: false }
      );
    },
    [mutateCandidateSearchListsResponse]
  );

  const currentOpportunityPageSavedList = useMemo(() => {
    return searchLists.find((savedList) => {
      return isEqualWith(savedList.query, currentOpportunityPageQuery, (objValue, othValue) => {
        if (Array.isArray(objValue) && Array.isArray(othValue)) {
          return objValue.length === othValue.length && objValue.every((value, index) => value === othValue[index]);
        }
      });
    });
  }, [searchLists, currentOpportunityPageQuery]);

  const handleAddList = useCallback(
    async ({ listName }: { listName: string }) => {
      if (!currentOpportunityPageQuery || currentOpportunityPageSavedList) return;

      setIsListAdded(false);
      const searchListPayload = {
        name: listName,
        alert: true,
        query: currentOpportunityPageQuery,
      };

      const response = await addCandidateSearchList({ searchList: searchListPayload });
      mutateCandidateSearchListsResponse(
        (prevData) => {
          if (!prevData) return;
          return {
            ...prevData,
            data: [...prevData.data, response.data],
          };
        },
        { revalidate: false }
      );
      setIsListAdded(true);
    },
    [currentOpportunityPageQuery, currentOpportunityPageSavedList, mutateCandidateSearchListsResponse]
  );

  const updateUserAction = useCallback(
    (props: UpdateUserActionProps) => {
      const { userAction } = props;
      if (userAction) {
        replace({ query: { ...query, user_action: userAction } }, undefined, { shallow: true });
      } else {
        const { user_action: _, ...restQuery } = query;
        replace({ query: restQuery }, undefined, { shallow: true });
      }
    },
    [replace, query]
  );

  const handleSaveList = useCallback(async () => {
    try {
      if (isCandidate) {
        await listNameModalInstance({ onSubmit: handleAddList });
        updateUserAction({ userAction: null });
      } else {
        await signInModalInstance();
      }
    } catch (error) {
      console.error(error);
      updateUserAction({ userAction: null });
    }
  }, [isCandidate, updateUserAction, signInModalInstance]);

  useEffect(() => {
    if (isInitialAuthLoading) return;

    if (Object.values(UserActionEnum).includes(queryUserAction as UserActionEnum)) {
      switch (queryUserAction) {
        case UserActionEnum.SAVE_LIST:
          handleSaveList();
          break;
        default:
          break;
      }
    }
  }, [isInitialAuthLoading, isCandidate, queryUserAction]);

  return {
    requestedPageType: requestedPageTypeRef.current,
    originalType: type,
    hasRequestedPageTypeChanged,
    type: contentType,
    category,
    q: searchQuery,
    debouncedSearch,
    sortBy,
    sortOrder,
    isSearchModalOpen,
    partialFilter,
    currentFilters,
    isFiltersApplied,
    isPartialFiltersApplied,
    canShowOnboardingBanner,
    initialQueryParamsReplaceLoading: initialQueryParamsReplaceLoadingRef.current,
    searchLists,
    currentOpportunityPageQuery,
    currentOpportunityPageSavedList,
    showSavedListsModal,
    isListAdded,
    handleTypeChange,
    applySavedListQuery,
    openSearchFilterModal,
    closeSearchFilterModal,
    replaceQuery,
    getFieldOptions,
    getFieldSubOptions,
    onPartialChange,
    onPartialUpdate,
    onPartialFiltersUpdate,
    handleFieldValueChange,
    handleSearchChange,
    handleClearSearch,
    onApplyPartialFilter,
    onResetAppliedFilters,
    onResetPartialFilters,
    handleSortByChange,
    closeOnboardingBanner,
    fetchMoreCompanies,
    setCompanySearchText,
    handleSaveList,
    handleDeleteList,
    handleUpdateList,
    openSavedListsModal,
    closeSavedListsModal,
    updateUserAction,
    handleAddList,
    filterState,
    setFilterState,
  };
};

export type UseGlobalSearchReturnType = ReturnType<typeof useGlobalSearchInternal>;
